import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { CommonUtils, PaginationUtils } from '@ocean/utils'
import { Card, ApiCardsDataResponse } from 'types/card'
import { createApiAction } from 'api'
import { CardUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import { searchRequirements, searchLibrary } from 'modules/Search/api/searchService'
import { SearchState } from './types'

const initialState: SearchState = {
  searchRequirementResults: {},
  searchLibraryResults: {},
}

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    searchRequirementResultsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCardsDataResponse>>
    ) {
      state.searchRequirementResults = action.payload
    },
    searchLibraryResultsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCardsDataResponse>>
    ) {
      state.searchLibraryResults = action.payload
    },
    clearSearchResults() {
      return initialState
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { searchRequirementResultsLoaded, searchLibraryResultsLoaded, clearSearchResults } =
  searchSlice.actions

export { clearSearchResults }
export default searchSlice.reducer

export const getSearchLibraryResultsData = createSelector(
  (searchResults: Card[] | undefined = undefined, currentPageId: number, pageSize: number) => ({
    searchResults,
    currentPageId,
    pageSize,
  }),
  ({ searchResults, currentPageId, pageSize }) => {
    if (!searchResults) return
    if (!currentPageId) return searchResults
    return PaginationUtils.getPageItems<Card>(searchResults, currentPageId, pageSize)
  }
)

export const getSearchRequirementsCardsPartition = createSelector(
  (searchResults: Card[] = []) => searchResults,
  searchResults =>
    CommonUtils.partition<Card>(searchResults, CardUtils.checkRequirementsGroupCardType)
)

export const getSearchRequirementsResultsData = createSelector(
  (searchResults: Card[] = [], currentPageId: number, pageSize: number) => ({
    searchResults: getSearchRequirementsCardsPartition(searchResults),
    currentPageId,
    pageSize,
  }),
  ({ searchResults, currentPageId, pageSize }) => {
    const [, requirements] = searchResults
    return PaginationUtils.getPageItems<Card>(requirements, currentPageId, pageSize)
  }
)

export const getSearchRequirementResults = (text?: string) => {
  return createApiAction({
    apiCalls: {
      search: () => searchRequirements(text),
    },
    payloadAction: searchRequirementResultsLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ search }) => search.searchRequirementResults.result,
  })
}

export const getSearchLibraryResults = (text?: string) => {
  return createApiAction({
    apiCalls: {
      search: () => searchLibrary(text),
    },
    payloadAction: searchLibraryResultsLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ search }) => search.searchLibraryResults.result,
  })
}
